// @ts-check
import { state, getDefaultState } from '../state';
import { clearBetslip } from '../index';
import { changeItemOdd, getBetbuilderRequest, getSwishBetBuilderRequest } from './items';
import PlaceModel from 'bto-models/betslip/place.model';
import BetBuilderPlaceModel from 'bto-models/betslip/betbuilder/betbuilder-place.model';
import AlgoBetBuilderPlaceModel from 'bto-models/betslip/betbuilder/algo-betbuilder-place.model';
import { getBetToastAdditionalTime, TOAST_NOTIFICATION_MESSAGE_STATUS } from 'bto-models/toast-notification';
import { BETSLIP_TYPES, BETSLIP_MSG_STATUS, BETSLIP_MESSAGE_TYPE, BETSLIP_MESSAGE_KEY, BETSLIP_ACCEPT_ODD_TYPE } from 'bto-models/betslip/const';
import PlaceBetRequest from '@bto-services/api/shared/place-bet.request';
import { setToastNotification, addUserBetslip } from '@bto-services/toast-notification';
import { userLoggedIn } from '@bto-services/user';
import { addBetslipMessage, addBetBuilderMessage } from './message.store';
import { assistedUserStore } from './assisted-user.store';
import { addQuickBetMessage } from './quickbet';
import { callLoginMethod } from '@bto-services/utils';
import Vue from 'vue';

/**
 * Check if user can place bet
 * @param {PlaceModel} placeModel - place model
 */
const validatePlacement = (placeModel) => {
	// If user is not logged in fail
	if (!userLoggedIn()) {
		const message = {
			type: BETSLIP_MESSAGE_TYPE.ERROR,
			message: 'Mobile.LoginFirst',
		}
		addBetslipMessage(BETSLIP_MESSAGE_KEY.LOGIN, message);
		callLoginMethod(APP_SETTINGS.settings.externalLoginMethod);
		return false;
	}
	// If total bet amount is not set fail
	if (placeModel.am <= 0) {
		const message = {
			type: BETSLIP_MESSAGE_TYPE.ERROR,
			message: 'Mobile.invalidAmount',
		};
		addBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE, message);
		return false;
	}
	// If we have freebet and incompatible items fail 
	if (placeModel.fbid && placeModel.ti < placeModel.absi.length) {
		return false;
	}
	// If single tab and not all singles stakes are set fail
	if (placeModel.bstid === BETSLIP_TYPES.SINGLE && placeModel.absi.length > placeModel.bs.length) {
		const message = {
			type: BETSLIP_MESSAGE_TYPE.ERROR,
			message: 'Mobile.invalidAmount',
		};
		addBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE, message);
		// invalid amount
		return false;
	}
	return true;
}

/**
 * Check odds for betbuilder items
 * @param {number} oldOdd - Previous odd value
 * @param {number} newOdd - New odd value
 * @returns {boolean} - Boolean
 */
function checkBetBuilderAcceptOdds(oldOdd, newOdd) {
	if ((state.acceptOddChange === BETSLIP_ACCEPT_ODD_TYPE.NO) && ((oldOdd) !== newOdd)) {
		return true
	}
	if ((state.acceptOddChange === BETSLIP_ACCEPT_ODD_TYPE.HIGHER) && ((oldOdd) < newOdd)) {
		return true
	}
	if ((state.acceptOddChange === BETSLIP_ACCEPT_ODD_TYPE.LOWER) && ((oldOdd) > newOdd)) {
		return true
	}
	return false;
}

/**
 * Save bet builder items in sportCast and validate each item
 * If validation fails throw error, and show message inside bet builder item
 */
function validateBetBuilderItems() {
	const betBuilderItems = Object.values(state.items).filter(i => i.betBuilder && !Object.keys(i.betBuilder).some(x => x.startsWith("sw_")));
	return Promise.all(
		betBuilderItems.map(i => getBetbuilderRequest(i, null, false)
			.execute()
			.then((resp) => {
				if (!resp.data || resp.data.Error || !resp.data.PayLoad || !resp.data.PayLoad.BetResponse) {
					const message = {
						type: BETSLIP_MESSAGE_TYPE.ERROR,
						message: Vue.prototype.$translate.text('BB.MatchError').replace('{matchName}', i.outcomeName),
					}
					addBetBuilderMessage(i.matchId, message);
					return Promise.reject('BetBuilder Price Validation Failed');
				}
				if (checkBetBuilderAcceptOdds((+i.odd), resp.data.PayLoad.Price)) {
					changeItemOdd(i, `${resp.data.PayLoad.Price}`);
					const message = {
						type: BETSLIP_MESSAGE_TYPE.WARNING,
						message: Vue.prototype.$translate.text('BB.MatchOddChanged').replace('{matchName}', i.outcomeName),
					}
					addBetBuilderMessage(i.matchId, message);
					return Promise.reject('BetBuilder Price Validation Failed');
				}
				const betBuilderPlaceModel = new BetBuilderPlaceModel({
					betResponse: resp.data.PayLoad.BetResponse,
					price: resp.data.PayLoad.Price,
					betBuilderItem: i,
					match: state.matchItems[i.matchId]
				});
				return Promise.resolve(betBuilderPlaceModel);
			})));
}

/**
 * Set quickbet notification before placement to prevent multiple place on same item
 * @param {string} itemKey - item key
 */
function setQuickBetNotification(itemKey, error = false) {
	const quickbetKey = `${APP_SETTINGS.player.id}_${itemKey}`;
	const betToastAdditionalTime = getBetToastAdditionalTime(BETSLIP_MSG_STATUS.PENDING);
	const expireTime = Date.now() + betToastAdditionalTime;
	setToastNotification(quickbetKey, {
		playerId: APP_SETTINGS.player.id,
		expireTime,
		message: error ? 'Mobile.generalError' : 'Mobile.waitingforauthorization',
		status: TOAST_NOTIFICATION_MESSAGE_STATUS.ACTION_NEEDED,
		keepBetslip: true,
		betslip: {
			status: error ? BETSLIP_MSG_STATUS.ERROR : BETSLIP_MSG_STATUS.PENDING,
		}
	});
}

/**
 * Get swish place model from bet item
 * @param {import('bto-models/betslip').BetslipItem} i - bet item
 * @param {string} [odd] - new odd
 * @returns 
 */
function getSwishPlaceModel(i, odd) {
	const betbuilderMarkets = Object.values(i.betBuilder);
	return new BetBuilderPlaceModel({
		betResponse: {
			Description: betbuilderMarkets.map(x => `${x.name} - ${x.description}`),
			LegDetails: betbuilderMarkets,
			Identifier: betbuilderMarkets.map(bb => bb.preMatchMarketId ? `m${bb.preMatchMarketId}` : `p${bb.matchPlayerId}`).join(', '),
		},
		price: odd || i.odd,
		betBuilderItem: i,
		swishModel: true,
		match: state.matchItems[i.matchId]
	});
}

/**
 * Save bet builder items from Swish and validate each item
 * If validation fails throw error, and show message inside bet builder item
 */
function validateSwishBetBuilderItems() {
	const betBuilderItems = Object.values(state.items).filter(i => i.betBuilder && Object.keys(i.betBuilder).some(ik => ik.startsWith("sw_")));
	return Promise.all(
		betBuilderItems.map(i => {
			if (Object.values(i.betBuilder).length === 1) {
				return Promise.reject('BetBuilder Price Validation Failed');
			}
			if (Object.values(i.betBuilder).length > 1) {
				return getSwishBetBuilderRequest(i)
					.execute()
					.then((resp) => {
						if (!resp.data) {
							const message = {
								type: BETSLIP_MESSAGE_TYPE.ERROR,
								message: Vue.prototype.$translate.text('BB.MatchError').replace('{matchName}', i.outcomeName),
							}
							addBetBuilderMessage(i.matchId, message);
							return Promise.reject('BetBuilder Price Validation Failed');
						}
						if (checkBetBuilderAcceptOdds((+i.odd), resp.data.o)) {
							changeItemOdd(i, `${resp.data.o}`);
							const message = {
								type: BETSLIP_MESSAGE_TYPE.WARNING,
								message: Vue.prototype.$translate.text('BB.MatchOddChanged').replace('{matchName}', i.outcomeName),
							}
							addBetBuilderMessage(i.matchId, message);
							return Promise.reject('BetBuilder Price Validation Failed');
						}
						return Promise.resolve(getSwishPlaceModel(i, resp.data.o));
					});
			}
		}));
}
/**
 * Get algo place model
 * @returns 
 */
function getAlgoPlaceModel() {
	const betBuilderItems = Object.values(state.items);
	return betBuilderItems.reduce((result, item) => {
		if (item.algo && state.algoItems[item.matchId] && state.algoItems[item.matchId].isActive && state.algoItems[item.matchId].singles.length > 1) {
			const algoModel = new AlgoBetBuilderPlaceModel({
				algoMatch: state.algoItems[item.matchId],
				match: state.matchItems[item.matchId]
			});
			result.push(algoModel);
		}
		return result;
	}, []);
}
/**
 * Get modified store from betslip store used for extracted single bet
 * @param {import('bto-models/betslip').BetslipStore} betslipStore - betslip store used for place model
 * @param {string} itemKey - item key
 */
function getModifiedStore(betslipStore, itemKey) {
	const matchId = betslipStore.items[itemKey].matchId;
	return {
		...getDefaultState(),
		selectedTab: betslipStore.selectedTab,
		acceptOddChange: betslipStore.acceptOddChange,
		manualAuth: betslipStore.manualAuth,
		note: betslipStore.note,
		items: { [itemKey]: betslipStore.items[itemKey] },
		matchItems: { [matchId]: betslipStore.matchItems[matchId] },
		singles: { [itemKey]: betslipStore.singles[itemKey] },
		singleBonuses: { [itemKey]: betslipStore.singleBonuses[itemKey] },
		algoItems: { [matchId]: betslipStore.algoItems[matchId] },
		totals: {
			stake: +betslipStore.singles[itemKey],
			maxOdds: betslipStore.items[itemKey].odd,
			minOdds: betslipStore.items[itemKey].odd,
		},
		bookedBetslipDetails: Object.assign({}, betslipStore.bookedBetslipDetails),
		extractedSingleItem: true,
	}
}

/**
 * Extract single item models from betslip place model  
 * !We mutate the acc array inside this function
 * @param {[import('bto-models/betslip').BetslipStore, PlaceModel][]} acc - acumulated state that we modify
 * @param {string} sk - item key
 * @param {PlaceModel} pm - main place model
 * @param {import('bto-models/betslip').BetslipStore} betslipStore - betslip store used for place model
 */
function extractSinglePlaceModels(acc, sk, pm, betslipStore) {
	const matchId = betslipStore.items[sk].matchId;
	const modifiedState = getModifiedStore(betslipStore, sk);

	const singlePlaceModel = new PlaceModel(modifiedState, pm.uid);
	if (state.items[sk].betBuilder) {
		const betBuilderPlaceModel = pm.bb.find(b => b.mid === matchId);
		singlePlaceModel.bb = [betBuilderPlaceModel];
	}

	if (betslipStore.raceOdds[sk]) {
		const raceOddState = getModifiedStore(betslipStore, sk);
		raceOddState.totals.maxOdds = raceOddState.totals.minOdds = betslipStore.items[sk].raceOdd.odd;
		raceOddState.raceOdds[sk] = betslipStore.raceOdds[sk];
		// New place model with
		const racePlaceModel = new PlaceModel(raceOddState, pm.uid);
		// Remove single item from here
		racePlaceModel.absi.shift();
		racePlaceModel.bs.shift();
		acc.push([raceOddState, racePlaceModel]);
	}
	acc.push([modifiedState, singlePlaceModel]);

	return acc;
}

/**
 * Extract single place items into separate place models
 * @param {PlaceModel} pm - place model
 * @param {import('bto-models/betslip').BetslipStore} betslipStore - betslip store used for place model
 * @returns {[import('bto-models/betslip').BetslipStore, PlaceModel][]}
 */
function extractSinglePlaceItems(pm, betslipStore) {
	// If we only have 1 item or If we don't have any singles proceeded with the bet
	if (pm.absi.length === 1 || pm.bs.length === 0) {
		return [[betslipStore, pm]];
	}
	/**
	 * @type {[import('bto-models/betslip').BetslipStore, PlaceModel][]}
	 */
	const placeModels = Object.keys(betslipStore.singles).reduce((acc, sk) => extractSinglePlaceModels(acc, sk, pm, betslipStore), []);

	// Check if folds are present in the original place model
	if (pm.f && Object.keys(pm.f).length > 0) {
		// Reset the signles array from original place model
		// Calculate total amount just from folds
		// Then add the original into the place models array
		const amount = Object.values(pm.bs).reduce((a, b) => a - (+b.am), pm.am);
		placeModels.push([{ ...betslipStore, singles: {}, singleBonuses: {}, extractedSingleItem: true }, { ...pm, bs: [], am: amount }]);
	}
	return placeModels;
}

/**
 * Place bet
 * @returns -> Promise if placement call was succesfull or not
 */
export const placeBet = async () => {
	const assistedBetUser = assistedUserStore.assistedUser;
	const placeModel = new PlaceModel(state, assistedBetUser.password);
	if (!validatePlacement(placeModel)) {
		return false;
	}
	// Copy betslip store
	const betslipStore = JSON.parse(JSON.stringify(state));

	let itemKey = '';
	try {
		// Neogames/Aspire portal validation
		if (window['ng_fw'] && window['ng_fw'].validateAction) {
			try {
				const action = await window['ng_fw'].validateAction('Bet');
				if (!action.isAllowed) {
					return false;
				}
			} catch (error) {
				return false;
			}
		}
		if (betslipStore.quickBetActive) {
			itemKey = Object.keys(betslipStore.items)[0];
			setQuickBetNotification(itemKey);
		}
		const betBuiderPlaceModels = await validateBetBuilderItems();
		placeModel.bb = betBuiderPlaceModels;
		const swishBetBuilderPlaceModels = await validateSwishBetBuilderItems();
		placeModel.bb = betBuiderPlaceModels.concat(swishBetBuilderPlaceModels);
		placeModel.abb = getAlgoPlaceModel();
		await Promise.all(extractSinglePlaceItems(placeModel, betslipStore).map(pb => new PlaceBetRequest(pb[1])
			.execute()
			.then(resp => {
				const key = `${APP_SETTINGS.player.id}_${pb[1].id}`;
				const quickbetKey = itemKey ? `${APP_SETTINGS.player.id}_${itemKey}` : '';
				const betStatus = resp.data.ResponseCode === BETSLIP_MSG_STATUS.NO_FUNDS ? BETSLIP_MSG_STATUS.NO_FUNDS : BETSLIP_MSG_STATUS.PENDING;
				const betToastAdditionalTime = getBetToastAdditionalTime(betStatus);
				const expireTime = Date.now() + betToastAdditionalTime;

				// Save betslip store in memory and local storage
				addUserBetslip(quickbetKey || key, pb[0]);
				if (quickbetKey) {
					addQuickBetMessage(key, itemKey);
				}
				setToastNotification(quickbetKey || key, {
					playerId: APP_SETTINGS.player.id,
					expireTime,
					message: resp.data.ResponseMessage,
					status: 1,
					keepBetslip: true,
					betslip: {
						status: betStatus,
					}
				});
			})));
	} catch (error) {
		console.error(error);
		// If placement call failed display general error message for quickbet mode
		if (itemKey) {
			setQuickBetNotification(itemKey, true);
		}
		return false;
	}
	clearBetslip();
	return true;
}
